import type { Exercise } from '../../types.ts';
export const batch5Reason: Exercise[] = [
  {
    id: 'reason-inspection-lines',
    category: 'reason',
    title: 'Inspection lines',
    topic: 'Inspection lines',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b5-inspection-lines',
        prompt:
          'Nine inspection stations must each connect directly to every other station by a separate cable. A cable joins exactly two stations. What is the minimum number of cables that must be removed so that the remaining network contains no closed loop? Stations may become disconnected.',
        answer: {
          kind: 'number',
          value: 28,
          unit: 'cables',
          tolerance: 0.0001,
        },
        explanation:
          'Exactly 28 cables must be removed. Allowing disconnected stations cannot improve this because a forest with more components retains fewer than eight edges.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch5.test.ts.',
      },
    ],
    reading: {
      title: 'Inspection lines — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A network can be represented by vertices for stations and edges for cables. A closed loop means a route that returns to its starting vertex without repeating an edge or any intermediate vertex. Removing loops is not the same as removing every route between distant stations. A branching network can connect all the stations while containing no cycle at all.',
            },
            {
              kind: 'paragraph',
              text: 'Begin with isolated vertices. Add edges one at a time without making a cycle. Each permitted edge joins two previously separate connected components: if a route already joined its endpoints, that route together with the new edge would make a cycle. Every added edge therefore reduces the number of components by exactly one. Starting with n components, at most n−1 such reductions are possible.',
            },
            {
              kind: 'equation',
              label: 'Acyclic edge count',
              lines: [
                {
                  left: 'edges',
                  right: 'vertices − components',
                  reason:
                    'Each edge merges two separate components. A nonempty network has at least one component.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'Four stations',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'A complete four-station network has six edges: AB, AC, AD, BC, BD and CD. Keep AB, AC and AD. Every other station connects through A, but there is no loop. Three edges have been removed.',
                },
                {
                  kind: 'table',
                  caption: 'Count components while building the retained network',
                  headers: ['Added edge', 'Components remaining'],
                  rows: [
                    ['None', '4'],
                    ['AB', '3'],
                    ['AC', '2'],
                    ['AD', '1'],
                  ],
                },
                {
                  kind: 'paragraph',
                  text: 'Trying to keep a fourth edge would connect two vertices already linked through A, closing a loop. The construction attains the upper bound of three retained edges.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'An acyclic network is called a forest; a connected forest is a tree. The counting argument applies even when the original problem allows disconnection. Disconnection leaves more than one component and therefore permits fewer retained edges, not more. To minimize removals we want to retain as many legal edges as possible.',
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Count the starting cables without counting AB and BA separately. Each of nine stations has eight partners, but that counts every cable twice, once from each endpoint. The complete network therefore has 9×8/2 = 36 cables.',
            },
            {
              kind: 'equation',
              label: 'Minimum removals',
              lines: [
                {
                  left: '36 − (9 − 1)',
                  right: '28',
                  reason: 'An acyclic nine-vertex network can retain at most eight edges.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'For an attainable construction, choose one central station and keep its eight connections to the other stations. Remove all cables between the eight outer stations. A route leaving an outer station must use its single retained edge; it cannot be part of a cycle. This star has eight edges, so the bound is reached.',
            },
            {
              kind: 'paragraph',
              text: 'Removing just one cable from a visible triangle does not settle the whole problem: the complete network has many overlapping cycles. Conversely, removing all but disjoint pairs would work but would discard more cables than necessary. The edge-count bound reasons about all cycles simultaneously.',
            },
            {
              kind: 'paragraph',
              text: 'Exactly 28 cables must be removed. Allowing disconnected stations cannot improve this because a forest with more components retains fewer than eight edges.',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'reason-batch-merge',
    category: 'reason',
    title: 'Combining files',
    topic: 'Combining files',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b5-batch-merge',
        prompt:
          'Five files have sizes 2, 3, 4, 7 and 9 MB. One operation combines any two available files into a new file whose size is their sum; its cost equals that sum in MB processed. Repeat until one file remains. Files cannot be split. What is the minimum total cost over all four operations?',
        answer: {
          kind: 'number',
          value: 55,
          unit: 'MB processed',
          tolerance: 0.0001,
        },
        explanation:
          'The minimum is 55 MB processed, attained by the four displayed merges and certified by the exchange-and-contraction argument.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch5.test.ts.',
      },
    ],
    reading: {
      title: 'Combining files — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Combining files has an immediate cost and a future cost. Once two files are combined, every later merge containing that combined file processes both original files again. A merge plan is a binary tree: original files are leaves and each internal node combines its two children. A leaf’s size is charged once for every merge between it and the final root.',
            },
            {
              kind: 'equation',
              label: 'Cost of a plan',
              lines: [
                {
                  left: 'total processed',
                  right: 'sum of each original size × its number of later merges',
                  reason: 'Small files can afford more repeated processing than large files.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Why does combining the two smallest available files first work? In any binary merge tree there is a deepest pair of sibling leaves. Moving smaller weights to deeper positions cannot raise total cost: swapping a larger deep weight with a smaller shallow weight reduces or preserves their weighted-depth sum. Thus an optimal tree can put the two smallest weights in that deepest sibling pair. Contract that pair into one leaf whose weight is their sum. What remains is the same kind of problem on one fewer file, plus the unavoidable cost of that pair’s merge.',
            },
            {
              kind: 'example',
              title: 'Three files of sizes 1, 2 and 10',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Combining 1 and 2 costs 3; merging the resulting 3 with 10 costs 13. Total: 16. Combining 2 and 10 first instead costs 12 and then 13, totaling 25. Both plans finish with 13 MB, but they process different amounts along the way.',
                },
                {
                  kind: 'table',
                  caption: 'Optimal smaller example',
                  headers: ['Operation', 'New size', 'Running cost'],
                  rows: [
                    ['1 + 2', '3', '3'],
                    ['3 + 10', '13', '16'],
                  ],
                },
                {
                  kind: 'paragraph',
                  text: 'The final file size alone cannot distinguish these plans. The small originals should pass through more levels.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'This exchange argument can be applied again after every contraction. It justifies the greedy rule rather than merely observing that the rule worked on a few examples. After a merge, the new combined file reenters the pool and must be compared with every remaining size. Sorting only the original files and pairing neighbors once is not the same algorithm.',
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'table',
              caption: 'Repeatedly combine the two smallest available sizes',
              headers: ['Available sizes before merge', 'Chosen pair', 'Cost'],
              rows: [
                ['2, 3, 4, 7, 9', '2 + 3', '5'],
                ['4, 5, 7, 9', '4 + 5', '9'],
                ['7, 9, 9', '7 + 9', '16'],
                ['9, 16', '9 + 16', '25'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'The two size-nine files at the third step are interchangeable for this cost objective. Either produces the same multiset of remaining sizes and therefore the same total cost. Distinguishing file identities changes the shape of the plan but not the minimum.',
            },
            {
              kind: 'equation',
              label: 'Total processing',
              lines: [
                {
                  left: '5 + 9 + 16 + 25',
                  right: '55 MB',
                  reason: 'Every internal merge contributes once.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'One useful check is that the final operation alone costs 25 MB, the sum of all original sizes. The earlier operations contribute another 30. Treating the answer as 25 would omit the repeated processing that makes this an optimization problem. The proof assumes every pair may be merged and the cost is exactly the sum; restrictions on order or fixed setup costs can change the problem.',
            },
            {
              kind: 'paragraph',
              text: 'The minimum is 55 MB processed, attained by the four displayed merges and certified by the exchange-and-contraction argument.',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'reason-seat-blocks',
    category: 'reason',
    title: 'The theater row',
    topic: 'The theater row',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b5-seat-blocks',
        prompt:
          'A row contains 12 seats. Exactly 4 people sit down, at most one per seat, with at least one empty seat between every pair of occupied seats. Count distinct sets of occupied seat numbers; who sits in each seat does not matter. How many sets are possible?',
        answer: {
          kind: 'number',
          value: 126,
          unit: 'sets',
          tolerance: 0.0001,
        },
        explanation:
          'There are 126 distinct occupied-seat sets. The correspondence supplies both completeness and freedom from duplicate counting.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch5.test.ts.',
      },
    ],
    reading: {
      title: 'The theater row — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Nonadjacent selections are difficult to count by choosing freely and then subtracting collisions: a seating pattern with several adjacent pairs may be subtracted repeatedly. A more reliable method constructs a one-to-one correspondence with selections that have no spacing restriction. A correspondence must work both ways; otherwise it may omit or duplicate patterns.',
            },
            {
              kind: 'paragraph',
              text: 'Write occupied seat numbers in increasing order as x₁ < x₂ < … < xₖ. The spacing rule says each next number is at least two greater. Remove the required gap before each person after the first: set yᵢ = xᵢ − (i−1). Then the y values are merely strictly increasing. The last possible y value is n−k+1 for a row of n seats. Conversely, adding i−1 back to each y restores every required gap.',
            },
            {
              kind: 'equation',
              label: 'Compress the compulsory gaps',
              lines: [
                {
                  left: 'valid patterns',
                  right: 'choose k positions from n−k+1',
                  reason:
                    'The subtraction removes k−1 compulsory empty places, not an empty place at each end.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'Two people in five seats',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'The valid pairs are (1,3), (1,4), (1,5), (2,4), (2,5), (3,5). Subtract one from the second position: these become all six ordinary pairs from 1,2,3,4.',
                },
                {
                  kind: 'table',
                  caption: 'A reversible correspondence',
                  headers: ['Original separated seats', 'Compressed seats'],
                  rows: [
                    ['1 and 3', '1 and 2'],
                    ['2 and 5', '2 and 4'],
                    ['3 and 5', '3 and 4'],
                  ],
                },
                {
                  kind: 'paragraph',
                  text: 'There is no required empty seat before the first person or after the last. Adding such a requirement would solve a stricter problem.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'To count ordinary selections of k distinct positions from m, count ordered selections m(m−1)…(m−k+1) and divide by k!, the number of orders of each selected set. This division is needed because the problem counts sets, not the identities or order of people. The symbol k! means the product 1×2×…×k.',
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'paragraph',
              text: 'With twelve seats and four people, compress three required gaps. Every legal occupied-seat set corresponds to an ordinary four-position selection from nine positions, and the inverse map restores a unique legal seating.',
            },
            {
              kind: 'equation',
              label: 'Choose four compressed positions',
              lines: [
                {
                  left: '9×8×7×6 / (4×3×2×1)',
                  right: '126',
                  reason:
                    'The numerator counts ordered selections; the denominator removes all 24 orders of each set.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'For example, original seats 2, 5, 7, 12 compress to 2, 4, 5, 9. The compressed positions may be adjacent; expanding their offsets restores the required gaps. This check illustrates why ordinary combinations are appropriate after compression.',
            },
            {
              kind: 'paragraph',
              text: 'An alternative check uses gaps. The four people leave eight empty seats; three are compulsory internal separators. The remaining five empty seats can be distributed among five gaps, including the two ends. This gives the same count of distributions, 126. No multiplication by 4! is appropriate because exchanging the people leaves the occupied set unchanged.',
            },
            {
              kind: 'paragraph',
              text: 'There are 126 distinct occupied-seat sets. The correspondence supplies both completeness and freedom from duplicate counting.',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'reason-liar-cards',
    category: 'reason',
    title: 'Three statements',
    topic: 'Three statements',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b5-liar-cards',
        prompt:
          'Exactly one of boxes A, B and C contains a token. A card on A says “The token is not in B.” A card on B says “The token is in A.” A card on C says “The token is not in A.” Exactly one of the three statements is true. Which box contains the token? Enter 1 for A, 2 for B or 3 for C.',
        answer: {
          kind: 'number',
          value: 2,
          unit: 'box number',
          tolerance: 0.0001,
        },
        explanation:
          'The answer is box B, encoded as 2. Both exhaustive evaluation and the complementary-pair argument identify it uniquely.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch5.test.ts.',
      },
    ],
    reading: {
      title: 'Three statements — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Logic puzzles become manageable when claims are separated from the facts they describe. A statement printed on a box need not describe that box, and a false statement is simply the negation of its full claim. The reliable method is to list every possible underlying state, evaluate all statements in each state, and retain only states satisfying the stated truth-count condition.',
            },
            {
              kind: 'paragraph',
              text: 'The phrase “exactly one is true” has two parts: at least one statement must hold and no second statement may hold. Do not start by guessing which speaker is truthful and forget to evaluate the others. A candidate solution must make the complete collection of constraints consistent.',
            },
            {
              kind: 'example',
              title: 'A simpler two-box check',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Suppose exactly one of X and Y contains a coin. The cards say “The coin is in X” and “The coin is not in Y.” These statements sound different but are equivalent under the exactly-one-coin rule: both are true when the coin is in X and both are false when it is in Y.',
                },
                {
                  kind: 'table',
                  caption: 'Truth depends on the underlying state',
                  headers: ['Coin location', 'In X', 'Not in Y'],
                  rows: [
                    ['X', 'True', 'True'],
                    ['Y', 'False', 'False'],
                  ],
                },
                {
                  kind: 'paragraph',
                  text: 'A condition of exactly one true card would make this smaller puzzle impossible. A condition of exactly two would identify X. Counting the outcomes exposes inconsistency as well as finding solutions.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Two statements can also be exact opposites. If one says P and the other says not P, exactly one of that pair must be true, provided both refer to the same proposition and the ordinary two-valued rules apply. Such a complementary pair can account for the entire permitted truth count before evaluating any remaining statement.',
            },
            {
              kind: 'paragraph',
              text: 'An exhaustive table is a proof here because there are only three possible locations and the prompt rules out every other location. It would not be sufficient to list just two promising cases. In a larger problem the same principle becomes a constraint-solving procedure: represent the hidden states and eliminate those violating a rule.',
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'table',
              caption: 'Test all token locations',
              headers: ['Location', 'A: not B', 'B: in A', 'C: not A', 'True count'],
              rows: [
                ['A', 'True', 'True', 'False', '2'],
                ['B', 'False', 'False', 'True', '1'],
                ['C', 'True', 'False', 'True', '2'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'The cards on B and C are opposites: “in A” and “not in A.” One of them is true for every possible token location. To keep the total at exactly one, A’s card must therefore be false. The negation of “not in B” is “in B,” which singles out B.',
            },
            {
              kind: 'paragraph',
              text: 'The table independently checks that deduction and rules out A and C. Notice that the card physically on the winning box is false. There was never a rule connecting the token location to the truth of its own card. Importing such a convention would add information absent from the prompt.',
            },
            {
              kind: 'paragraph',
              text: 'The answer is box B, encoded as 2. Both exhaustive evaluation and the complementary-pair argument identify it uniquely.',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'reason-cache-checks',
    category: 'reason',
    title: 'A hidden threshold',
    topic: 'A hidden threshold',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b5-cache-checks',
        prompt:
          'A device has an unknown integer threshold T from 0 through 63 inclusive. A test chooses an integer x and returns whether T is at least x. Later tests may depend on earlier results. What is the smallest number of tests that guarantees identifying T exactly in the worst case?',
        answer: {
          kind: 'number',
          value: 6,
          unit: 'tests',
          tolerance: 0.0001,
        },
        explanation:
          'Six tests are necessary and sufficient. The guarantee concerns the longest path, not an average over an assumed distribution of thresholds.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch5.test.ts.',
      },
    ],
    reading: {
      title: 'A hidden threshold — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A yes-or-no test partitions the remaining possibilities into two groups. After observing the answer, only one group remains possible. An adaptive strategy is a decision tree: internal nodes specify tests, branches specify results, and leaves specify final conclusions. Guaranteeing identification means distinct possible thresholds must end at distinct leaves.',
            },
            {
              kind: 'paragraph',
              text: 'One binary test has at most two outcomes. Two tests have at most four possible answer sequences; each extra test can at most double that number. Therefore a strategy using at most k tests on every path has at most 2ᵏ leaves. This is a lower bound on information requirements, not by itself a construction. The allowed tests must actually be capable of making useful partitions.',
            },
            {
              kind: 'equation',
              label: 'Capacity of k binary tests',
              lines: [
                {
                  left: 'maximum number of result patterns',
                  right: '2ᵏ',
                  reason:
                    'Each candidate threshold needs its own terminal answer sequence, so the number of candidates cannot exceed this maximum.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'Eight possible thresholds, 0 through 7',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Ask whether T is at least 4. A yes leaves 4–7; a no leaves 0–3. Split the surviving group at its midpoint, and split the resulting pair once more. Three tests distinguish all eight possibilities.',
                },
                {
                  kind: 'table',
                  caption: 'Worst-case candidates remaining',
                  headers: ['Tests completed', 'Candidates'],
                  rows: [
                    ['0', '8'],
                    ['1', '4'],
                    ['2', '2'],
                    ['3', '1'],
                  ],
                },
                {
                  kind: 'paragraph',
                  text: 'Testing thresholds in ascending order would be legal but could require many more answers. The midpoint approach controls the largest remaining group rather than hoping for an early success.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'A threshold test is especially useful because the candidates have a known order. If the surviving interval runs from a to b, testing an integer near its middle partitions it into nearly equal intervals. In a problem with only strange restricted tests, an information lower bound might not be attainable. Here ordered comparisons supply the matching construction.',
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'paragraph',
              text: 'There are 64 possibilities, including both endpoints 0 and 63. Five binary tests distinguish at most 32 outcomes, so they cannot guarantee success. Six tests distinguish at most 64, leaving open the possibility of an optimal six-test strategy.',
            },
            {
              kind: 'paragraph',
              text: 'Test x=32 first, then split the surviving 32-integer interval into halves of sixteen, then eight, four, two and one. For example, a no to 32 leads to x=16; a yes leads to x=48. Every branch follows the same size-halving pattern.',
            },
            {
              kind: 'equation',
              label: 'Matching bound and construction',
              lines: [
                {
                  left: 'minimum tests',
                  right: '6',
                  reason: 'The candidate counts are 64, 32, 16, 8, 4, 2, 1.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'No extra confirmation test is needed once exactly one candidate remains: it is determined by the preceding answers and the stipulated range. This matters at endpoints such as zero. The strategy identifies a threshold even if no test ever returns yes.',
            },
            {
              kind: 'paragraph',
              text: 'Six tests are necessary and sufficient. The guarantee concerns the longest path, not an average over an assumed distribution of thresholds.',
            },
          ],
        },
      ],
    },
  },
];
